import { datadogRum } from '@datadog/browser-rum';
import type { EventHint, SeverityLevel } from '@sentry/react';
import * as Sentry from '@sentry/react';
import axios, { type AxiosError } from 'axios';

import { Main } from 'services/Analytics/core';
import {
    BLACK_LIST_HANDLED_ERROR_NAMES,
    ERROR_LEVELS,
    IGNORE_ERRORS_LIST,
    IGNORE_URLS_LIST,
    IGNORED_ERROR_RESPONSES,
    SENTRY_MIDDLEWARE,
} from 'services/Sentry/constants';

import { checkFbBots, checkIsOldDevices, getCustomTags } from './helpers';
import type { errorType, ExtendedErrorEvent, Extras, IError, Tags } from './interfaces';

declare interface iSentry {
    sentryDSN: string;
    env: 'stage' | 'prod' | string;
    release?: string;
    tracesSampleRate?: number;
    autoSessionTracking?: boolean;
}

export default class SentryClient {
    sentryDSN: string;
    env: 'stage' | 'prod' | string;
    release?: string;
    tracesSampleRate?: number;
    private isInited: boolean;

    private sentry: any;

    constructor({ sentryDSN, env, release, tracesSampleRate }: iSentry) {
        this.sentryDSN = sentryDSN;
        this.env = env || 'stage';
        this.release = release || '1.0.0';
        this.tracesSampleRate = tracesSampleRate || 1.0;
        this.isInited = false;
        this.sentry = Sentry;
    }

    init() {
        if (this.isInited) return;

        Sentry.init({
            dsn: this.sentryDSN,
            integrations: [Sentry.browserTracingIntegration()],
            environment: this.env,
            debug: this.env !== 'prod',
            release: this.release,
            tracesSampleRate: this.tracesSampleRate,
            maxBreadcrumbs: 10,
            beforeSend: (event: ExtendedErrorEvent, hint: EventHint) => {
                SentryClient.filterEventsBeforeSend(event, hint);

                const sessionId = datadogRum.getInternalContext()?.session_id;

                if (sessionId) {
                    const q = encodeURIComponent(`@session.id:${sessionId}`);

                    event.extra = {
                        ...event.extra,
                        datadogSessionLink: `https://app.datadoghq.com/rum/explorer?query=${q}`,
                    };
                }

                return event;
            },
            ignoreErrors: IGNORE_ERRORS_LIST || [],
            denyUrls: IGNORE_URLS_LIST || [],
        });

        this.isInited = true;
    }

    public logError(
        error: IError,
        type: errorType,
        errorLevel: SeverityLevel,
        extras?: Extras,
        customTags?: Tags,
        customPrepareErrorByCode?: string
    ) {
        const customTagsArr = customTags || getCustomTags(error, type, extras);
        const preparedError = customPrepareErrorByCode || `[${type}] ${error}`;

        SentryClient.getAnalyticData().then((analyticData) =>
            this.sentry.withScope((scope: Sentry.Scope) => {
                extras && scope.setExtras({ ...extras, ...(analyticData && analyticData) });

                scope.setTag('ERROR_TYPE', type);
                scope.setTag('aws_id', analyticData ? analyticData.aws_id : null);
                scope.setLevel(errorLevel);

                customTagsArr.length && customTagsArr.forEach(([tag, value]) => scope.setTag(tag, value));

                this.sentry.captureMessage(preparedError);
            })
        );
    }

    public sentrySagaLog(error: Error, sagaName: string, errorLevel: SeverityLevel = ERROR_LEVELS.ERROR) {
        const type = SENTRY_MIDDLEWARE;
        const sentryTitle = `[${type}] [${sagaName}] ${error}`;
        let customTags: Tags = [['sagaName', sagaName]];
        let extras: Extras | undefined;

        if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError;

            extras = {
                requestURL: axiosError.config?.url,
                response: axiosError.response,
                status: axiosError.response?.status,
                message: axiosError.message,
            };
            customTags = [...customTags, ['API_ERROR_CODE', extras?.status as string]];
        }

        this.logError(error, type, errorLevel, extras, customTags, sentryTitle);
    }

    private static async getAnalyticData() {
        const isInitAmazonAnalytic = Main.checkIsAlreadyInited({ name: 'amazon' });

        return (isInitAmazonAnalytic && (await Main?.getDefaultAmazonAnalyticPurchaseData?.())) || null;
    }

    private static filterEventsBeforeSend(event: ExtendedErrorEvent, hint: EventHint) {
        const error = hint.originalException;

        if (
            (typeof error === 'string' && SentryClient.matchSubstring(error)) ||
            checkIsOldDevices(event) ||
            checkFbBots() ||
            SentryClient.shouldIgnoreErrorByResponse(event)
        ) {
            return null;
        }

        return event;
    }

    private static matchSubstring(error: string) {
        return BLACK_LIST_HANDLED_ERROR_NAMES.some((item) => error.includes(item));
    }

    private static shouldIgnoreErrorByResponse(error: ExtendedErrorEvent): boolean {
        const responseData = error?.extra?.response?.data;

        if (!responseData) return false;

        return IGNORED_ERROR_RESPONSES.some((ignored) =>
            Object.entries(ignored).every(([key, value]) => responseData[key] === value)
        );
    }
}
